import { env } from "@postdaily/env/web";
import type { CaptureResult, PostHog, Properties } from "posthog-js";
import { z } from "zod";

import { readConsent } from "./cookie-consent";

/**
 * PostHog is enabled only when a project key is configured. Local/dev builds
 * without a key run normally — every analytics call becomes a no-op.
 */
export const posthogEnabled = Boolean(env.VITE_POSTHOG_KEY);

/**
 * The no-login capability-token pages: the client review portal and the
 * creator stats page. Neither visitor is a PostDaily user — they never agreed
 * to our analytics, and the URL they arrive on IS the capability. Analytics
 * never loads here at all, so no pageview, autocapture click or session
 * replay can be recorded. See {@link redactCapabilityTokens} for the second
 * layer.
 */
export function isAnalyticsExemptPath(pathname: string) {
  return pathname.startsWith("/review/") || pathname.startsWith("/creator/");
}

/**
 * Routes worth session replay and exception autocapture — everything a signed-in
 * user touches.
 *
 * Public marketing pages deliberately get neither. Each add-on is a separate
 * script fetched from PostHog's CDN (`posthog-recorder.js`, `surveys.js`,
 * `dead-clicks-autocapture.js`, `exception-autocapture.js`, `web-vitals.js`),
 * and on a throttled phone those five requests cost more than the landing page
 * itself. A brochure page needs a pageview; it does not need a replay of
 * someone reading it.
 */
const PUBLIC_PREFIXES = [
  "/about",
  "/alternatives",
  "/blog",
  "/careers",
  "/changelog",
  "/contact",
  "/cookie-policy",
  "/data-deletion",
  "/features",
  "/gdpr",
  "/help",
  "/integrations",
  "/pricing",
  "/privacy",
  "/terms",
];

export function isPublicMarketingPath(pathname: string) {
  return (
    pathname === "/" ||
    pathname.endsWith("-scheduler") ||
    PUBLIC_PREFIXES.some(
      (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`),
    )
  );
}

/**
 * `/review/<token>` and `/creator/<token>` — the whole path segment is the
 * capability. The prefix is kept in the replacement so a redacted URL still
 * says which portal it was.
 */
const CAPABILITY_PATH_PATTERN = /\/(review|creator)\/[^/?#\s"]+/g;
/**
 * `?token=` carries a workspace invite and a password-reset token; `?state=`
 * an OAuth state; `?request=` an AI-agent consent request (`/authorize`) and
 * `?approval=` its one-time approval code. All
 * are single-use capabilities that must not be copied into an analytics store.
 */
const TOKEN_PARAM_PATTERN =
  /([?&](?:token|state|request|approval)=)[^&#\s"]+/gi;
/**
 * The same parameters URL-encoded inside another one — a signed-out visit to
 * `/authorize?request=…` becomes `/login?redirect=%2Fauthorize%3Frequest%3D…`.
 */
const ENCODED_TOKEN_PARAM_PATTERN =
  /((?:%3F|%26)(?:token|state|request|approval)%3D)[^&#\s"%]+/gi;

/**
 * Strip capability tokens out of a URL-bearing analytics value.
 *
 * PostHog attaches `$current_url` and `$referrer` (plus their `$initial_*` and
 * session-entry twins) to every captured event, so without this a review link,
 * an invitation, or a password-reset link would be persisted in a third party's
 * analytics database simply because someone opened it — and anyone with project
 * access could then replay it. Exported for tests.
 */
export function redactCapabilityTokens(value: string) {
  return value
    .replace(CAPABILITY_PATH_PATTERN, "/$1/[redacted]")
    .replace(TOKEN_PARAM_PATTERN, "$1[redacted]")
    .replace(ENCODED_TOKEN_PARAM_PATTERN, "$1[redacted]");
}

function hasCapabilityToken(value: string) {
  return (
    value.includes("/review/") ||
    value.includes("/creator/") ||
    /[?&](?:token|state|request|approval)=/i.test(value) ||
    /(?:%3F|%26)(?:token|state|request|approval)%3D/i.test(value)
  );
}

/**
 * A captured property that carries a URL. PostHog's `Property` is a union of
 * every JSON value, so the string arm is parsed out at this boundary rather
 * than narrowed inline — only strings can hold a token.
 */
const urlPropertySchema = z.string();

/**
 * Redact capability tokens from every property of an outgoing event.
 *
 * Runs on `$pageview`, autocapture, `$exception` and web-vitals alike, so it
 * covers `$current_url`, `$referrer`, their `$initial_*` twins, and any future
 * URL-bearing property without this needing to enumerate them.
 */
export function scrubCapabilityTokens(
  event: CaptureResult | null,
): CaptureResult | null {
  if (!event?.properties) {
    return event;
  }

  let scrubbed: Properties | null = null;

  for (const [key, value] of Object.entries(event.properties)) {
    const parsed = urlPropertySchema.safeParse(value);

    if (!parsed.success || !hasCapabilityToken(parsed.data)) {
      continue;
    }

    scrubbed ??= { ...event.properties };
    scrubbed[key] = redactCapabilityTokens(parsed.data);
  }

  return scrubbed ? { ...event, properties: scrubbed } : event;
}

/**
 * The loaded, initialized SDK — or null when analytics is off for this page.
 *
 * `posthog-js` is ~200 KiB of JavaScript that no page needs in order to render,
 * so it is imported dynamically and never reaches the entry chunk. Everything
 * downstream goes through {@link withPostHog}, which resolves this promise, so
 * a call made before the SDK finishes loading still lands rather than being
 * dropped.
 */
let clientPromise: Promise<PostHog | null> | null = null;

/**
 * Load and initialize PostHog once, on the client only.
 *
 * Safe to call repeatedly (the promise is the guard) and during SSR — it
 * resolves to null, because `window` is undefined on the Cloudflare Worker that
 * renders the app.
 */
export function initPostHog(): Promise<PostHog | null> {
  const projectKey = env.VITE_POSTHOG_KEY;

  if (clientPromise) {
    return clientPromise;
  }

  if (!projectKey || typeof window === "undefined") {
    return Promise.resolve(null);
  }

  if (isAnalyticsExemptPath(window.location.pathname)) {
    return Promise.resolve(null);
  }

  /*
   * Consent, checked HERE rather than in `AnalyticsProvider`.
   *
   * `withPostHog` calls this function itself, so gating only the provider's
   * effect would still let `track("checkout_started")` from the pricing page
   * boot the whole SDK for someone who never agreed to it. This is the single
   * chokepoint every path runs through.
   *
   * It sits above the dynamic import deliberately: without consent we must not
   * even fetch posthog-js, because loading it is what writes the cookie.
   *
   * Returning early leaves `clientPromise` unset — it is assigned only on the
   * success path below — so a later grant retries cleanly instead of being
   * memoized as "off" for the life of the document.
   */
  if (!readConsent().analytics) {
    return Promise.resolve(null);
  }

  const lean = isPublicMarketingPath(window.location.pathname);

  clientPromise = import("posthog-js")
    .then(({ default: posthog }) => {
      posthog.init(projectKey, {
        api_host: env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com",
        // Modern defaults: SPA pageviews (history_change), session recording
        // opt-in, web vitals, and autocapture — see
        // posthog.com/docs/libraries/js/config.
        defaults: "2026-05-30",
        // Error tracking: wrap window.onerror + onunhandledrejection →
        // $exception. Off on the marketing site, which loads its own add-on
        // script to do it (see PUBLIC_PREFIXES).
        capture_exceptions: !lean,
        capture_dead_clicks: !lean,
        capture_performance: lean ? false : undefined,
        disable_session_recording: lean,
        disable_surveys: lean,
        /*
         * Skip the /flags request on public pages.
         *
         * PostHog's remote config can switch add-ons on from project settings,
         * and it does: dead-click autocapture kept loading from the CDN even
         * with `capture_dead_clicks: false` here. Nothing in this app reads a
         * feature flag, so the request only ever bought us scripts we had
         * explicitly turned off. Everything the SDK needs is configured above.
         */
        advanced_disable_flags: lean,
        // Only create person profiles for identified (logged-in) users;
        // anonymous marketing-page events stay cheaper and don't mint throwaway
        // profiles.
        person_profiles: "identified_only",
        // Second layer, and the one that survives a client-side navigation into
        // a tokenized route after the SDK is already running: no event leaves
        // the browser with a live capability token in it. `before_send` rather
        // than the deprecated `sanitize_properties`.
        before_send: scrubCapabilityTokens,
      });
      return posthog;
    })
    .catch(() => null);

  return clientPromise;
}

/**
 * Turn session replay on for a visitor who started on a marketing page and has
 * since navigated into the app. Init options are fixed for the life of the
 * document, so this is how the lean config gets upgraded without a reload.
 */
export function enableSessionTelemetry() {
  withPostHog((client) => {
    client.startSessionRecording();
  });
}

/**
 * Run `fn` against the initialized SDK, loading it first if it is not up yet.
 *
 * Fire-and-forget by design: analytics must never make a call site wait, and a
 * blocked or failed CDN fetch must never surface as an application error.
 */
/**
 * Stop analytics and remove what it stored.
 *
 * Withdrawing consent has to erase, not merely stop: a toggle that halts future
 * capture while leaving the identifier on the device has not withdrawn
 * anything. `opt_out_capturing` alone is not enough either — it works by
 * *writing* an opt-out flag into the same storage we are removing.
 *
 * So: stop the running client, drop its identity, then delete every key it
 * owns. On the next load the gate in `initPostHog` means it never starts.
 */
export function disableAnalytics() {
  if (typeof window === "undefined") {
    return;
  }

  if (clientPromise) {
    void clientPromise.then((client) => {
      if (!client) {
        return;
      }

      /*
       * Order is load-bearing, and getting it wrong looks like success.
       *
       * The SDK stays alive in memory for the rest of the document, and its
       * persistence layer rewrites the cookie and localStorage entry after any
       * state change. Deleting first and opting out second — or even opting out
       * first — left both back on disk within a second, verified on production.
       *
       * So switch persistence to memory BEFORE anything else: from then on the
       * SDK writes only to a store that dies with the page, and the delete
       * below stays deleted.
       */
      client.set_config({ persistence: "memory" });
      client.opt_out_capturing();
      client.reset(true);
      forgetPostHogStorage();
    });

    // The client is memoized for the life of the document; a later grant has
    // to be able to start a fresh one rather than reuse an opted-out instance.
    clientPromise = null;
    return;
  }

  forgetPostHogStorage();
}

/** Delete PostHog's own cookie and storage entries, whatever they are named. */
function forgetPostHogStorage() {
  const isPostHogKey = (key: string) => key.startsWith("ph_");

  for (const storage of [window.localStorage, window.sessionStorage]) {
    try {
      for (const key of Object.keys(storage).filter(isPostHogKey)) {
        storage.removeItem(key);
      }
    } catch {
      // Storage unavailable — nothing was written either.
    }
  }

  for (const entry of document.cookie.split(";")) {
    const name = entry.split("=")[0]?.trim();

    if (name && isPostHogKey(name)) {
      expireCookie(name);
    }
  }
}

/**
 * Expire a cookie whatever domain it was scoped to.
 *
 * A cookie can only be deleted from the exact domain that set it, and PostHog
 * defaults to `cross_subdomain_cookie`, which scopes it to the registrable
 * domain — so a visitor on `staging.postdaily.app` is carrying a cookie set on
 * `.postdaily.app`. Expiring it on the current host alone silently leaves it in
 * place, which is the difference between withdrawing consent and appearing to.
 *
 * Observed on staging: a `ph_*` cookie set by the production site survived a
 * host-scoped delete and was still being sent.
 *
 * So walk the hostname's suffixes and expire it on each. Writing to a domain
 * the browser rejects (a public suffix, or one we do not own) is a silent no-op
 * rather than an error, so the extra attempts cost nothing.
 */
function expireCookie(name: string) {
  const labels = window.location.hostname.split(".");
  const scopes: (string | undefined)[] = [undefined];

  for (let i = 0; i < labels.length - 1; i += 1) {
    scopes.push(`.${labels.slice(i).join(".")}`);
  }

  for (const domain of scopes) {
    document.cookie = `${name}=; Max-Age=0; path=/${
      domain ? `; domain=${domain}` : ""
    }`;
  }
}

/**
 * sessionStorage keys remembering what this tab last sent as identity and
 * group properties. The `ph_` prefix is deliberate: withdrawing consent
 * deletes every `ph_` key along with PostHog's own storage.
 */
export const IDENTITY_SENT_KEY = "ph_postdaily_identity_sent";
export const GROUP_SENT_KEY = "ph_postdaily_group_sent";

/**
 * Forget who is signed in: on every sign-out and on account deletion.
 *
 * `reset()` gives the browser a fresh anonymous id and clears super
 * properties. The sent-markers go too, or the next person to sign in on this
 * tab would be skipped as "already identified".
 */
export function resetAnalyticsIdentity() {
  withPostHog((client) => {
    client.reset();
  });

  if (typeof window === "undefined") {
    return;
  }

  try {
    window.sessionStorage.removeItem(IDENTITY_SENT_KEY);
    window.sessionStorage.removeItem(GROUP_SENT_KEY);
  } catch {
    // Storage unavailable — nothing was written either.
  }
}

export function withPostHog(fn: (client: PostHog) => void) {
  if (!posthogEnabled) {
    return;
  }

  void initPostHog().then((client) => {
    if (client) {
      fn(client);
    }
  });
}
